import React, { useRef, useEffect, Suspense } from 'react';
import { Image as DreiImage, useGLTF, TransformControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { SplatMesh } from '@sparkjsdev/spark';
import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
// Shared SparkRenderer singleton — managed in SplatRenderer.tsx
import { EnsureSparkRenderer } from '../plugins/marble-worlds/SplatRenderer';
import { useWorldStore } from '../stores/worldStore';
import { broadcastObjectLock, broadcastObjectUnlock, broadcastObjectTransform } from '../services/worldBroadcast';

interface ContentItem {
  id: string;
  url: string;
  type: 'image' | 'audio' | 'model' | 'splat' | 'ply';
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  isLocked?: boolean;
  collidable?: boolean;
  autoFit?: boolean;
  seat?: boolean;
  mobileUrl?: string;
  environmentMesh?: boolean;
  materialColors?: Record<string, string>;
  materialTextures?: Record<string, { maps: any; scale?: number }>;
}

interface ContentDisplayProps {
  content: ContentItem;
  isSelected: boolean;
  onSelect: () => void;
  transformMode?: 'translate' | 'rotate' | 'scale';
  orbitRef?: React.RefObject<any>;
  onTransformChange?: (updates: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: [number, number, number] }) => void;
  // Show a ground ring under seats (builder only, so authors can see which
  // objects are marked as places to sit).
  showSeatMarker?: boolean;
  // Reports the model's material slots so the UI can offer a colour per element.
  onMaterialSlots?: (slots: { name: string; color: string }[]) => void;
}

const gxTexCache = new Map<string, THREE.Texture>();

function gxTexture(url: string, repeat: number, srgb: boolean) {
  const key = `${url}|${repeat}|${srgb}`;
  let t = gxTexCache.get(key);
  if (!t) {
    t = new THREE.TextureLoader().load(url);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat, repeat);
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    gxTexCache.set(key, t);
  }
  return t;
}

// Model content
const ModelContent: React.FC<{
  url: string; onSelect: () => void; collidable?: boolean; autoFit?: boolean; contentId?: string;
  materialColors?: Record<string, string>;
  materialTextures?: Record<string, { maps: any; scale?: number }>;
  environmentMesh?: boolean;
  mobileUrl?: string;
  onMaterialSlots?: (slots: { name: string; color: string }[]) => void;
}> = ({ url, onSelect, collidable, autoFit, contentId, materialColors, materialTextures, environmentMesh, mobileUrl, onMaterialSlots }) => {
  // Pick the variant from the GPU's real limit rather than sniffing the user
  // agent — Quest Browser reports itself as desktop Linux, and phone caps vary.
  const { gl } = useThree();
  const maxTex = gl?.capabilities?.maxTextureSize ?? 8192;
  const srcUrl = mobileUrl && maxTex <= 4096 ? mobileUrl : url;
  const { scene } = useGLTF(srcUrl);
  const cloned = React.useMemo(() => scene.clone(), [scene]);
  // Tag every mesh so the collision systems (VR push-out, first-person
  // raycast) treat this model as solid when enabled
  useEffect(() => {
    cloned.traverse((child: any) => {
      // Parts named *_nocol opt out of collision even on a solid model. Venue
      // seating uses it: collision is per-mesh AABB, so a row of chairs becomes
      // a wall across the hall and you could never walk to a seat to sit in it.
      if (child.isMesh) {
        // A sky never collides and never casts or receives shadows.
        child.userData.collidable = !environmentMesh && !!collidable && !/_nocol\b/i.test(child.name || '');
        child.castShadow = !environmentMesh;
        child.receiveShadow = !environmentMesh;
      }
    });
  }, [cloned, collidable, environmentMesh]);

  // Per-material-slot recolouring. Every mesh sharing a slot repaints together,
  // so recolouring "cushion" changes all 200 seats at once.
  //
  // useGLTF caches the parsed GLB and Object3D.clone() shares its materials, so
  // painting in place would repaint every copy of this model in the world (and
  // poison the cache for the next one). Clone each material the first time we
  // touch it and remember its original colour so overrides can be cleared.
  useEffect(() => {
    cloned.traverse((child: any) => {
      if (!child.isMesh || !child.material) return;
      const list = Array.isArray(child.material) ? child.material : [child.material];
      const owned = list.map((m: any) => {
        // Resolve the lit material we own for this slot. When the model is
        // acting as sky, child.material is our unlit stand-in, so recover the
        // original through __gxStd rather than cloning the stand-in.
        let mine = m.userData?.__gxStd || m;
        if (!mine.userData?.__gxOwned) {
          mine = mine.clone();
          mine.userData = {
            ...(m.userData || {}),
            __gxOwned: true,
            __gxBase: `#${m.color.getHexString()}`,
            // Glowing slots (neon, screens) are read almost entirely from their
            // emissive colour, so recolouring base alone would look like nothing
            // happened. Remember whether this slot glows, and tint it too.
            __gxEmissive: mine.emissive && mine.emissive.getHex() !== 0
              ? `#${mine.emissive.getHexString()}` : null,
            // The model's own baked maps. Clearing a slot override must put
            // these back — nulling them strips the texture the GLB shipped with
            // and the model renders as flat base colour.
            __gxMaps: {
              map: m.map ?? null,
              normalMap: m.normalMap ?? null,
              roughnessMap: m.roughnessMap ?? null,
              aoMap: m.aoMap ?? null,
              metalnessMap: m.metalnessMap ?? null,
            },
          };
        }
        if (environmentMesh) {
          // Sky mesh: unlit so world lighting can't darken it, DoubleSide so
          // it's visible from the inside (back faces are what you look at when
          // you're stood within it), and fog-exempt so it doesn't wash out.
          let env = mine.userData.__gxEnvMat;
          if (!env) {
            env = new THREE.MeshBasicMaterial({
              map: mine.userData.__gxMaps?.map ?? mine.map ?? null,
              color: 0xffffff,
              side: THREE.DoubleSide,
              toneMapped: false,
              fog: false,
            });
            env.name = mine.name;
            env.userData = { __gxStd: mine };
            mine.userData.__gxEnvMat = env;
          }
          env.needsUpdate = true;
          return env;
        }

        const want = materialColors?.[mine.name];
        mine.color.set(want || mine.userData.__gxBase);
        if (mine.userData.__gxEmissive) {
          mine.emissive.set(want || mine.userData.__gxEmissive);
        }

        // Optional PBR set per slot. The normal and roughness maps are what
        // actually add depth — a colour map alone just adds noise at this poly
        // count. Models are box-projected at 1 UV unit per metre, so `scale`
        // means tiles-per-metre and matches across every surface.
        const tex = materialTextures?.[mine.name];
        if (tex?.maps?.colorUrl) {
          const r = tex.scale ?? 0.5;
          mine.map = gxTexture(tex.maps.colorUrl, r, true);
          mine.normalMap = tex.maps.normalUrl ? gxTexture(tex.maps.normalUrl, r, false) : null;
          mine.roughnessMap = tex.maps.roughnessUrl ? gxTexture(tex.maps.roughnessUrl, r, false) : null;
          mine.aoMap = tex.maps.aoUrl ? gxTexture(tex.maps.aoUrl, r, false) : null;
          mine.metalnessMap = tex.maps.metallicUrl ? gxTexture(tex.maps.metallicUrl, r, false) : null;
          // a textured slot is tinted by its colour, so keep it white unless asked
          if (!want) mine.color.set('#ffffff');
        } else {
          // No override: restore whatever the GLB itself came with.
          const own = mine.userData.__gxMaps || {};
          mine.map = own.map ?? null;
          mine.normalMap = own.normalMap ?? null;
          mine.roughnessMap = own.roughnessMap ?? null;
          mine.aoMap = own.aoMap ?? null;
          mine.metalnessMap = own.metalnessMap ?? null;
          mine.color.set(want || mine.userData.__gxBase);
        }
        mine.needsUpdate = true;
        return mine;
      });
      child.material = Array.isArray(child.material) ? owned : owned[0];
    });
  }, [cloned, materialColors, materialTextures, environmentMesh]);

  // Report the slots this model exposes so the UI can offer a swatch per element.
  useEffect(() => {
    if (!onMaterialSlots) return;
    const found = new Map<string, string>();
    cloned.traverse((child: any) => {
      if (!child.isMesh || !child.material) return;
      const list = Array.isArray(child.material) ? child.material : [child.material];
      list.forEach((m: any) => {
        if (m?.name && !found.has(m.name)) {
          found.set(m.name, m.userData?.__gxBase || `#${m.color.getHexString()}`);
        }
      });
    });
    onMaterialSlots([...found].map(([name, color]) => ({ name, color })));
  }, [cloned, onMaterialSlots]);
  // One-time scale normalization for freshly-added library/upload models.
  // Poly Pizza models and user uploads arrive in arbitrary native units — a
  // "chair" can be ~5m tall at scale 1, which engulfs the view (hiding the
  // ground) and makes the transform gizmo unusable. Measure the loaded
  // geometry once, fit its largest dimension to ~1.6m, rest it on the ground,
  // then clear the flag so it never re-fits.
  useEffect(() => {
    if (!autoFit || !contentId) return;
    // Measure the original (unmounted) gltf scene so the bbox is in the model's
    // native units, independent of our render group's current scale.
    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const st = useWorldStore.getState();
    if (!isFinite(maxDim) || maxDim <= 0) {
      st.updateContentItem(contentId, { autoFit: false });
      return;
    }
    // A sky mesh wraps the world rather than sitting in it: size it to a large
    // radius and centre it on the origin instead of resting it on the ground.
    // Models we author in real metres (doors, furniture) carry a gxRealScale
    // flag in their glTF extras: keep them 1:1 and only rest them on the ground.
    let realScale = false;
    scene.traverse((o: any) => { if (o.userData?.gxRealScale) realScale = true; });
    const fit = realScale && !environmentMesh ? 1 : (environmentMesh ? 160 : 1.6) / maxDim;
    const cur = st.contentItems[contentId];
    const pos = cur?.position || [0, 0, -2];
    // A sky centres on the world origin; a prop rests on the ground where placed.
    const nextPos: [number, number, number] = environmentMesh
      ? [0, 0, 0]
      : [pos[0], -box.min.y * fit, pos[2]];
    st.updateContentItem(contentId, { scale: fit, position: nextPos, autoFit: false });
    const wid = st.actualWorldId;
    if (wid) {
      broadcastObjectTransform(wid, contentId, {
        position: nextPos,
        rotation: (cur?.rotation as [number, number, number]) || [0, 0, 0],
        scale: [fit, fit, fit],
      });
    }
  }, [scene, autoFit, contentId]);
  return <primitive object={cloned} onClick={onSelect} />;
};

// Splat content using Spark SplatMesh (supports .splat, .spz, .ply, .ksplat)
const SplatInner: React.FC<{ url: string; onSelect: () => void }> = ({ url, onSelect }) => {
  const groupRef = useRef<THREE.Group>(null);
  const splatRef = useRef<SplatMesh | null>(null);
  const isPresenting = useXR((s) => !!s.session);

  useEffect(() => {
    if (!groupRef.current) return;
    let disposed = false;

    // In VR: cap splat count to protect Quest GPU budget.
    // Two simultaneous SPZ sorts at full resolution = 2fps on Quest.
    // lod: false — createLodSplats() takes 18–28s for large files and increases
    // primitive count, causing GPU pressure. Precompute LoD server-side instead.
    const mesh = new SplatMesh({
      url,
      lod: false,
      maxSplats: isPresenting ? 25000 : undefined,
      onLoad: () => {
        if (!disposed) {
          console.log('[Spark] Splat loaded:', url);
        }
      },
    });

    // Cap spherical harmonics for performance (same as MarbleWorldRenderer)
    mesh.maxSh = 0;
    // Reduce splats behind viewer — more aggressive in VR where you truly can't see behind you
    mesh.behindFoveate = isPresenting ? 16 : 4;

    splatRef.current = mesh;
    groupRef.current.add(mesh);

    return () => {
      disposed = true;
      if (groupRef.current && splatRef.current) groupRef.current.remove(splatRef.current);
      splatRef.current?.dispose();
      splatRef.current = null;
    };
  }, [url, isPresenting]);

  return (
    <>
      <EnsureSparkRenderer />
      <group ref={groupRef} onClick={onSelect}>
        <mesh visible={false}>
          <boxGeometry args={[2, 2, 2]} />
          <meshBasicMaterial />
        </mesh>
      </group>
    </>
  );
};

// PLY content (non-gaussian-splat PLY — regular mesh/point cloud)
const PLYContent: React.FC<{ url: string; isSelected: boolean; onSelect: () => void }> = ({ url, isSelected, onSelect }) => {
  const pointsRef = useRef<THREE.Points>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  const [geometry, setGeometry] = React.useState<THREE.BufferGeometry | null>(null);
  const [hasColors, setHasColors] = React.useState(false);
  const [hasFaces, setHasFaces] = React.useState(false);

  useEffect(() => {
    const loader = new PLYLoader();
    loader.load(
      url,
      (geo) => {
        geo.computeVertexNormals();
        geo.computeBoundingBox();
        if (geo.boundingBox) {
          const center = new THREE.Vector3();
          geo.boundingBox.getCenter(center);
          geo.translate(-center.x, -center.y, -center.z);
        }
        setGeometry(geo);
        setHasColors(!!geo.attributes.color);
        setHasFaces(!!geo.index && geo.index.count > 0);
      },
      undefined,
      (err) => console.error('[PLY] Load error:', err)
    );
    return () => {
      if (geometry) geometry.dispose();
    };
  }, [url]);

  if (!geometry) {
    return (
      <mesh onClick={onSelect}>
        <sphereGeometry args={[0.3, 8, 8]} />
        <meshBasicMaterial color="#00c2b3" wireframe />
      </mesh>
    );
  }

  if (hasFaces) {
    return (
      <group onClick={onSelect}>
        <mesh ref={meshRef} geometry={geometry}>
          {hasColors ? (
            <meshStandardMaterial vertexColors roughness={0.6} metalness={0.1} />
          ) : (
            <meshStandardMaterial color="#cccccc" roughness={0.6} metalness={0.1} />
          )}
        </mesh>
      </group>
    );
  }

  return (
    <group onClick={onSelect}>
      <points ref={pointsRef} geometry={geometry}>
        <pointsMaterial
          size={3}
          vertexColors={hasColors}
          color={hasColors ? undefined : '#00c2b3'}
          sizeAttenuation
        />
      </points>
    </group>
  );
};

// Determine if a URL is a Gaussian splat PLY (vs regular PLY)
// Gaussian splat PLYs are typically large and from specific sources
// For now, use file extension: .ply uploaded as 'splat' type uses GaussianSplats3D
const isSplatFormat = (url: string): boolean => {
  return url.match(/\.(splat|spz|ksplat)$/i) !== null;
};

const ContentDisplay: React.FC<ContentDisplayProps> = ({ content, isSelected, onSelect, transformMode = 'translate', orbitRef, onTransformChange, showSeatMarker, onMaterialSlots }) => {
  const groupRef = useRef<THREE.Group>(null!);
  const isPresenting = useXR((s) => !!s.session);
  const editLock = useWorldStore((st) => st.editLocks[content.id]);
  const snapSettings = useWorldStore((st) => st.snapSettings);
  const lastStreamRef = useRef(0);
  const s = content.scale;

  let inner: React.ReactNode = null;

  if (content.type === 'image') {
    inner = (
      <>
        <DreiImage url={content.url} scale={2} onClick={onSelect} transparent />
        {isSelected && (
          <mesh position={[0, 0, -0.01]}>
            <planeGeometry args={[2.2, 2.2]} />
            <meshBasicMaterial color="#00c2b3" transparent opacity={0.3} />
          </mesh>
        )}
      </>
    );
  } else if (content.type === 'model') {
    inner = (
      <Suspense fallback={<mesh><boxGeometry args={[0.5, 0.5, 0.5]} /><meshBasicMaterial color="#00c2b3" wireframe /></mesh>}>
        <ModelContent url={content.url} onSelect={onSelect} collidable={content.collidable}
          autoFit={content.autoFit} contentId={content.id}
          materialColors={content.materialColors} materialTextures={content.materialTextures}
          environmentMesh={content.environmentMesh} mobileUrl={content.mobileUrl}
          onMaterialSlots={onMaterialSlots} />
      </Suspense>
    );
  } else if (content.type === 'splat') {
    // Spark SplatMesh handles all formats: .splat, .spz, .ply, .ksplat
    inner = <SplatInner url={content.url} onSelect={onSelect} />;
  } else if (content.type === 'ply') {
    inner = <PLYContent url={content.url} isSelected={isSelected} onSelect={onSelect} />;
  } else if (content.type === 'audio') {
    inner = (
      <mesh onClick={onSelect}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshBasicMaterial color={isSelected ? "#00c2b3" : "#00E5FF"} wireframe={isSelected} />
      </mesh>
    );
  }

  if (!inner) return null;

  const groupEl = (
    <group
      ref={groupRef}
      position={content.position}
      rotation={content.rotation}
      scale={[s, s, s]}
      // objectId: lets VR hand tools find the selected model's group.
      userData={content.seat ? { seat: true, objectId: content.id } : { objectId: content.id }}
      // Rays only. Sphere pointers (hand grab / fingertip touch) test a mesh's
      // bounding BOX, so inside a big environment model they "hit" it at 0 m and
      // the combined pointer turns the laser off. VR moving of content goes
      // through VRGrab / VRHandManipulator, not these pointer events.
      pointerEventsType={{ deny: ['grab', 'touch'] }}
    >
      {inner}
      {isSelected && content.type !== 'image' && (
        <mesh>
          <boxGeometry args={[2, 2, 2]} />
          <meshBasicMaterial color="#00c2b3" transparent opacity={0.1} wireframe />
        </mesh>
      )}
    </group>
  );

  // Builder-only affordance: a teal ring on the ground under objects marked as
  // seats, so authors can see which objects are places to sit. Rendered as a
  // sibling (world space) so it isn't affected by the object's scale.
  const seatMarker = showSeatMarker && content.seat ? (
    <mesh position={[content.position[0], 0.03, content.position[2]]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={2}>
      <ringGeometry args={[0.38, 0.52, 44]} />
      <meshBasicMaterial color="#00c2b3" transparent opacity={0.5} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  ) : null;

  if (isSelected && !content.isLocked && !editLock && !isPresenting && onTransformChange) {
    // Model beside the gizmo, not inside it (see ScenePrimitive in WorldBuilder).
    return (
      <>
      {groupEl}
      <TransformControls
        object={groupRef}
        mode={transformMode}
        translationSnap={snapSettings.grid ? snapSettings.gridSize : null}
        rotationSnap={snapSettings.rotation ? (snapSettings.rotationAngle * Math.PI / 180) : null}
        onMouseDown={() => {
          (window as any).__transformDragging = true;
          if (orbitRef?.current) orbitRef.current.enabled = false;
          const wid = useWorldStore.getState().actualWorldId;
          if (wid) broadcastObjectLock(wid, content.id);
        }}
        onChange={() => {
          if (!(window as any).__transformDragging || !groupRef.current) return;
          const now = performance.now();
          if (now - lastStreamRef.current < 66) return;
          lastStreamRef.current = now;
          const wid = useWorldStore.getState().actualWorldId;
          if (!wid) return;
          const g = groupRef.current;
          broadcastObjectTransform(wid, content.id, {
            position: [g.position.x, g.position.y, g.position.z],
            rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
            scale: [g.scale.x, g.scale.y, g.scale.z],
          });
        }}
        onMouseUp={() => {
          setTimeout(() => { (window as any).__transformDragging = false; }, 50);
          if (orbitRef?.current) orbitRef.current.enabled = true;
          if (groupRef.current) {
            const p = groupRef.current.position;
            const r = groupRef.current.rotation;
            const sc = groupRef.current.scale;
            onTransformChange({
              position: [p.x, p.y, p.z],
              rotation: [r.x, r.y, r.z],
              scale: [sc.x, sc.y, sc.z],
            });
          }
          const wid = useWorldStore.getState().actualWorldId;
          if (wid) broadcastObjectUnlock(wid, content.id);
        }}
      />
      </>
    );
  }

  if (seatMarker) {
    return <>{groupEl}{seatMarker}</>;
  }
  return groupEl;
};

export default ContentDisplay;
